import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { useAskAgain } from "../../api/refresh";
import { companyIdentityQueryOptions, updateCompanyIdentity } from "../../api/settings";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { Card, CardHeader } from "../../components/ui/card";
import { Field, Input, Select } from "../../components/ui/field";
import { CURRENCIES, IDENTITY_FIELD_LABELS, INDUSTRY_IDS, INDUSTRY_LABELS } from "../../domain/settings";
import { fieldErrorMessage } from "../../lib/form";
import { isTimeZone } from "../../lib/format";
import { showToast } from "../../lib/toast";

export const Route = createFileRoute("/settings/identity")({
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: ({ context }) => context.queryClient.query({ ...companyIdentityQueryOptions(), staleTime: "static" }),
	head: () => ({ meta: [{ title: "Identity · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Identity"] },
	component: IdentityPage,
});

function optionalText(maxLength: number) {
	return z.string().trim().max(maxLength, `Use at most ${maxLength} characters`);
}

/** The issuer address card's fields. Filling in any of them asks for the ones in `REQUIRED_ADDRESS_FIELDS`. */
const ADDRESS_FIELDS = ["streetAddress", "city", "state", "zipCode", "country"] as const;
const REQUIRED_ADDRESS_FIELDS = ["streetAddress", "city", "zipCode", "country"] as const;

// Only the company name is required, so the display names can be saved before there is an address. An address is
// either left empty or complete, because invoices cannot use half of one.
const identityFormSchema = z
	.object({
		companyName: optionalText(120).min(1, `${IDENTITY_FIELD_LABELS.companyName} is required`),
		legalName: optionalText(160),
		streetAddress: optionalText(200),
		city: optionalText(100),
		state: optionalText(100),
		zipCode: optionalText(20),
		country: optionalText(100),
		timeZone: z.string().refine(isTimeZone, "Pick a time zone"),
		currency: z.enum(CURRENCIES),
		industry: z.enum(INDUSTRY_IDS).or(z.literal("")),
	})
	.superRefine((identity, context) => {
		if (ADDRESS_FIELDS.every(field => identity[field] === "")) {
			return;
		}
		for (const field of REQUIRED_ADDRESS_FIELDS) {
			if (identity[field] === "") {
				context.addIssue({
					code: "custom",
					path: [field],
					message: `${IDENTITY_FIELD_LABELS[field]} is required`,
				});
			}
		}
	});

// Every IANA time zone the browser knows. The list leaves out UTC, which is the default, so it goes first.
const TIME_ZONES = ["UTC", ...Intl.supportedValuesOf("timeZone").filter(zone => zone !== "UTC")];

interface TextFieldProps {
	readonly label: string;
	readonly name: string;
	readonly value: string;
	readonly error: string | undefined;
	readonly autoComplete: string;
	readonly full?: boolean;
	readonly onBlur: () => void;
	readonly onChange: (value: string) => void;
}

function TextField({ label, name, value, error, autoComplete, full, onBlur, onChange }: TextFieldProps) {
	return (
		<Field label={label} error={error} full={full}>
			<Input
				name={name}
				value={value}
				autoComplete={autoComplete}
				onBlur={onBlur}
				onChange={event => {
					onChange(event.target.value);
				}}
			/>
		</Field>
	);
}

function IdentityPage() {
	const { data: identity } = useSuspenseQuery(companyIdentityQueryOptions());
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const mutation = useMutation({
		mutationFn: updateCompanyIdentity,
		// The sidebar shows the company name straight away; the audit entry arrives through a queue a moment after
		// this answer, so it is asked again as it catches up.
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["settings"] }),
				askAgain(["settings", "audit-log"]),
			]),
	});

	const form = useForm({
		defaultValues: { ...identity },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: identityFormSchema, onSubmit: identityFormSchema },
		// Typing a city makes the untouched street, ZIP code and country invalid without showing why. Save stays enabled,
		// and pressing it marks every field touched, so their messages appear.
		canSubmitWhenInvalid: true,
		// A new edit makes an earlier error stale.
		listeners: {
			onChange: () => {
				mutation.reset();
			},
		},
		onSubmit: async ({ value }) => {
			try {
				// parse() applies the schema's trimming, so surrounding spaces are not saved.
				await mutation.mutateAsync(identityFormSchema.parse(value));
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			showToast("Identity saved.");
		},
	});

	return (
		<>
			<PageHeader
				title="Identity"
				description="Who the company is on invoices, contracts and reports, and the defaults every section works with."
			/>
			<form
				noValidate
				className="flex flex-col gap-3.5"
				onSubmit={event => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<Card>
					<CardHeader title="Display" />
					<div className="grid grid-cols-2 gap-2.5 p-4">
						<p className="col-span-2 text-caption text-ink-2">
							The company name is shown in the sidebar and on reports. The legal entity name goes on
							contracts and formal documents.
						</p>
						<form.Field name="companyName">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.companyName}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="organization"
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
						<form.Field name="legalName">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.legalName}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="off"
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
					</div>
				</Card>

				<Card>
					<CardHeader title="Issuer address" />
					<div className="grid grid-cols-2 gap-2.5 p-4">
						<p className="col-span-2 text-caption text-ink-2">
							Printed on invoices and contracts as the address of the issuing party. Leave it empty until
							you need it; once you start, only the state is optional.
						</p>
						<form.Field name="streetAddress">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.streetAddress}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="street-address"
									full
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
						<form.Field name="city">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.city}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="address-level2"
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
						<form.Field name="state">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.state}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="address-level1"
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
						<form.Field name="zipCode">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.zipCode}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="postal-code"
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
						<form.Field name="country">
							{field => (
								<TextField
									label={IDENTITY_FIELD_LABELS.country}
									name={field.name}
									value={field.state.value}
									error={fieldErrorMessage(field.state.meta)}
									autoComplete="country-name"
									onBlur={field.handleBlur}
									onChange={field.handleChange}
								/>
							)}
						</form.Field>
					</div>
				</Card>

				<Card>
					<CardHeader title="Business profile" />
					<div className="grid grid-cols-2 gap-2.5 p-4">
						<p className="col-span-2 text-caption text-ink-2">
							The time zone decides when a date rolls over and how the audit log shows times. Finance
							converts the cash into the primary currency, at the European Central Bank's reference rates.
						</p>
						<form.Field name="timeZone">
							{field => (
								<Field
									label={IDENTITY_FIELD_LABELS.timeZone}
									error={fieldErrorMessage(field.state.meta)}
								>
									<Select
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={event => {
											field.handleChange(event.target.value);
										}}
									>
										{/* A saved zone the browser lists under another name still needs its option. */}
										{TIME_ZONES.includes(field.state.value) ? null : (
											<option value={field.state.value}>{field.state.value}</option>
										)}
										{TIME_ZONES.map(zone => (
											<option key={zone} value={zone}>
												{zone}
											</option>
										))}
									</Select>
								</Field>
							)}
						</form.Field>
						<form.Field name="currency">
							{field => (
								<Field label={IDENTITY_FIELD_LABELS.currency}>
									<Select
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={event => {
											const next = CURRENCIES.find(code => code === event.target.value);
											if (next !== undefined) {
												field.handleChange(next);
											}
										}}
									>
										{CURRENCIES.map(code => (
											<option key={code} value={code}>
												{code}
											</option>
										))}
									</Select>
								</Field>
							)}
						</form.Field>
						<form.Field name="industry">
							{field => (
								<Field label={IDENTITY_FIELD_LABELS.industry} full>
									<Select
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={event => {
											field.handleChange(
												INDUSTRY_IDS.find(id => id === event.target.value) ?? "",
											);
										}}
									>
										<option value="">Not set</option>
										{INDUSTRY_IDS.map(id => (
											<option key={id} value={id}>
												{INDUSTRY_LABELS[id]}
											</option>
										))}
									</Select>
								</Field>
							)}
						</form.Field>
					</div>
				</Card>

				{mutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{mutation.error.message}
					</p>
				) : null}
				<div>
					<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
						{([canSubmit, isSubmitting]) => (
							<Button type="submit" variant="primary" disabled={!canSubmit || isSubmitting}>
								{isSubmitting ? "Saving…" : "Save identity"}
							</Button>
						)}
					</form.Subscribe>
				</div>
			</form>
		</>
	);
}
